import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardActionArea from '@material-ui/core/CardActionArea';
import { Link } from 'react-router-dom';
import PageTitle from '../Utils/smsTitle';
import { SubHeader } from '../../Containers/SubHeader';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Divider from '@material-ui/core/Divider';
import Chip from '@material-ui/core/Chip';
import CircularProgress from '@material-ui/core/CircularProgress';
import { surgeryService } from '../../services/surgeryService';

const useStyles = makeStyles((theme) => ({
  root: {
    margin: 22,
    padding: 20,
  },
  kpiRow: {
    marginBottom: 24,
  },
  kpiCard: {
    textAlign: 'center',
    padding: 16,
    borderRadius: 8,
    transition: 'box-shadow 0.2s',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    },
  },
  kpiValue: {
    fontSize: '2rem',
    fontWeight: 700,
  },
  kpiLabel: {
    fontSize: '0.85rem',
    color: '#666',
    fontWeight: 500,
    marginTop: 4,
  },
  sectionTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    marginBottom: 12,
    color: '#333',
  },
  placeholder: {
    padding: 24,
    textAlign: 'center',
    color: '#999',
    border: '2px dashed #e0e0e0',
    borderRadius: 8,
    minHeight: 200,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  quickActions: {
    display: 'flex',
    gap: 12,
    marginBottom: 24,
  },
  actionBtn: {
    textDecoration: 'none',
    fontSize: '0.85rem',
    fontWeight: 500,
  },
}));

const kpiCards = [
  { label: 'Pending Referrals', key: 'pending_referrals', color: '#FF9800', link: '/surgery/queue' },
  { label: 'Scheduled Surgeries', key: 'scheduled', color: '#2196F3', link: '/surgery/list?status=0' },
  { label: 'In Theatre Now', key: 'in_theatre', color: '#4CAF50', link: '/surgery/list?status=1' },
  { label: 'Completed Surgeries', key: 'completed', color: '#009688', link: '/surgery/list?status=2' },
  { label: 'Cancelled Surgeries', key: 'cancelled', color: '#F44336', link: '/surgery/list?status=9' },
  { label: 'Cancelled Referrals', key: 'cancelled_referrals', color: '#9C27B0', link: '/surgery/queue?status=9' },
];

const formatReferralName = (item) => {
  const first = item.first_name || item.patient_first_name || '';
  const last = item.last_name || item.patient_last_name || '';
  const name = `${first} ${last}`.trim();
  return name || item.patient_number || 'Unknown patient';
};

const formatUrgency = (urgency) => {
  if (urgency === 2 || urgency === '2') return 'Urgent';
  if (urgency === 1 || urgency === '1') return 'High';
  return 'Normal';
};

const getStatusLabel = (status) => {
  if (status === 1 || status === '1') return 'In Progress';
  if (status === 0 || status === '0') return 'Scheduled';
  if (status === 2 || status === '2') return 'Completed';
  if (status === 9 || status === '9') return 'Cancelled';
  return 'Scheduled';
};

const SurgeryDashboard = () => {
  const classes = useStyles();

  const [stats, setStats] = useState({});
  const [theatreSchedule, setTheatreSchedule] = useState([]);
  const [pendingReferrals, setPendingReferrals] = useState([]);
  const [upcomingSurgeries, setUpcomingSurgeries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      try {
        const [statsRes, referralsRes, inTheatreRes, upcomingRes] = await Promise.all([
          surgeryService.getDashboardStats(),
          surgeryService.listReferrals({ status: 0, per_page: 5 }),
          surgeryService.listSurgeries(1, { status: 1, per_page: 5 }),
          surgeryService.listSurgeries(1, { status: 0, per_page: 5 }),
        ]);

        if (cancelled) return;
        setStats(statsRes.data || {});
        setPendingReferrals(Array.isArray(referralsRes.data) ? referralsRes.data : referralsRes.data?.referrals || []);
        setTheatreSchedule(Array.isArray(inTheatreRes.data?.surgeries) ? inTheatreRes.data.surgeries : []);
        setUpcomingSurgeries(Array.isArray(upcomingRes.data?.surgeries) ? upcomingRes.data.surgeries : []);
      } catch (error) {
        console.error('Error fetching surgery dashboard data:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', marginTop: 50 }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div>
      <PageTitle title="Surgery Dashboard" />
      <SubHeader title="Surgery" subTitle="Dashboard" />
      <Paper className={classes.root} elevation={1}>
        {/* KPI Cards Row */}
        <Grid container spacing={2} className={classes.kpiRow}>
          {kpiCards.map((card) => (
            <Grid item xs={12} sm={6} md key={card.key}>
              <Link to={card.link} style={{ textDecoration: 'none' }}>
                <Card className={classes.kpiCard} style={{ backgroundColor: '#fff' }}>
                  <CardActionArea>
                    <CardContent style={{ padding: 16 }}>
                      <Typography className={classes.kpiValue} style={{ color: card.color }}>
                        {stats[card.key] || 0}
                      </Typography>
                      <Typography className={classes.kpiLabel}>
                        {card.label}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Link>
            </Grid>
          ))}
        </Grid>


        {/* Main Content: Theatre Schedule + Lists */}
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography className={classes.sectionTitle}>Today's Theatre Schedule</Typography>
            <Paper variant="outlined" style={{ padding: 16, marginBottom: 16 }}>
              <List disablePadding>
                {theatreSchedule.map((item, index) => {
                  const startTime = item.estimated_start_time || item.start_time || '-';
                  const procedureName = item.service_name || item.procedure || 'Unknown procedure';
                  const surgeonName = item.lead_surgeon || item.referring_doctor_first_name ? `${item.lead_surgeon || ''}${item.lead_surgeon ? ' ' : ''}${item.lead_surgeon_last_name || ''}`.trim() : '';
                  const theatreName = item.theatre_name || item.theatre || '';
                  const statusLabel = getStatusLabel(item.status);
                  return (
                    <React.Fragment key={item.id || index}>
                      <ListItem alignItems="flex-start" style={{ paddingLeft: 0, paddingRight: 0 }}>
                        <ListItemText
                          primary={
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.85rem' }}>
                              <span style={{ fontWeight: 600 }}>{startTime} &mdash; {procedureName}</span>
                              <Chip size="small" label={statusLabel} color={statusLabel === 'In Progress' ? 'primary' : 'default'} style={{ fontSize: '0.75rem', height: 20 }} />
                            </div>
                          }
                          secondary={`Patient: ${formatReferralName(item)} | Surgeon: ${surgeonName} | Location: ${theatreName}`}
                          secondaryTypographyProps={{ style: { fontSize: '0.75rem' } }}
                        />
                      </ListItem>
                      {index < theatreSchedule.length - 1 && <Divider />}
                    </React.Fragment>
                  );
                })}
              </List>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography className={classes.sectionTitle}>Pending Referrals (Urgent First)</Typography>
            <Paper variant="outlined" style={{ padding: 16 }}>
              <List disablePadding>
                {pendingReferrals.map((item, index) => {
                  const referralDate = item.created_at ? new Date(item.created_at).toLocaleDateString() : item.date || '-';
                  return (
                    <React.Fragment key={item.referral_id || item.id || index}>
                      <ListItem alignItems="flex-start" style={{ paddingLeft: 0, paddingRight: 0 }}>
                        <ListItemText
                          primary={
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.85rem' }}>
                              <span style={{ fontWeight: 600 }}>{item.service_name || item.procedure || 'Referral'}</span>
                              <Chip size="small" label={formatUrgency(item.urgency)} color={formatUrgency(item.urgency) === 'Urgent' ? 'secondary' : 'default'} style={{ fontSize: '0.75rem', height: 20 }} />
                            </div>
                          }
                          secondary={`Patient: ${formatReferralName(item)} | Referral Date: ${referralDate}`}
                          secondaryTypographyProps={{ style: { fontSize: '0.75rem' } }}
                        />
                      </ListItem>
                      {index < pendingReferrals.length - 1 && <Divider />}
                    </React.Fragment>
                  );
                })}
              </List>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography className={classes.sectionTitle}>Upcoming Surgeries</Typography>
            <Paper variant="outlined" style={{ padding: 16 }}>
              <List disablePadding>
                {upcomingSurgeries.map((item, index) => {
                  const dateLabel = item.operation_date || item.date || '-';
                  const timeLabel = item.estimated_start_time || item.start_time || '-';
                  const procedureName = item.service_name || item.procedure || 'Scheduled surgery';
                  const surgeonName = item.lead_surgeon || '';
                   return (
                    <React.Fragment key={item.id || index}>
                      <ListItem alignItems="flex-start" style={{ paddingLeft: 0, paddingRight: 0 }}>
                        <ListItemText
                          primary={
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.85rem' }}>
                              <span style={{ fontWeight: 600 }}>{procedureName}</span>
                              <Typography variant="body2" color="textSecondary" style={{ fontSize: '0.75rem' }}>{dateLabel} @ {timeLabel}</Typography>
                            </div>
                          }
                          secondary={`Patient: ${formatReferralName(item)} | Surgeon: ${surgeonName}`}
                          secondaryTypographyProps={{ style: { fontSize: '0.75rem' } }}
                        />
                      </ListItem>
                      {index < upcomingSurgeries.length - 1 && <Divider />}
                    </React.Fragment>
                  );
                })}
              </List>
            </Paper>
          </Grid>
        </Grid>
      </Paper>
    </div>
  );
};

export default SurgeryDashboard;
